import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/lib/config";
import { track } from "@/lib/track";
import AppHeader from "@/components/AppHeader";

const SPECIALTIES = ["General Physician", "Anaesthesia", "Paediatrics", "Emergency", "Cardiology", "Orthopaedics", "OBG", "Radiology", "ICU / Critical Care", "Nurse — General Ward", "Nurse — ICU", "Nurse — OT", "Clinical Assistant", "Alternative Medicine"];
const FACILITY_TYPES = ["Hospital", "Clinic", "Nursing Home", "Diagnostic Center"];
const DEGREES = ["MBBS", "MD", "MS", "DM", "MCh", "DNB", "BDS", "MDS", "BAMS (Ayurveda)", "BHMS (Homeopathy)", "BUMS (Unani)", "BSMS (Siddha)", "BNYS (Yoga & Naturopathy)", "BSc Nursing", "MSc Nursing", "GNM", "ANM", "BPT", "MPT", "Diploma in Clinical Assistant", "Other"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { supported_cities } = useConfig();
  const [role, setRole] = useState(null);
  const [tos, setTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [city, setCity] = useState("");

  // Pick up the role chosen on the landing page (via startGoogleAuth) and pre-select it.
  useEffect(() => {
    try {
      const intended = localStorage.getItem("lc_intended_role");
      if (intended === "professional" || intended === "facility") {
        setRole(intended);
        localStorage.removeItem("lc_intended_role");
      }
    } catch (err) {
      console.warn("localStorage unavailable in onboarding:", err);
    }
  }, []);

  // Professional fields
  const [profRole, setProfRole] = useState("doctor");
  const [specialty, setSpecialty] = useState("");
  const [degree, setDegree] = useState("");
  const [years, setYears] = useState("");
  const [rate, setRate] = useState("");
  const [phone, setPhone] = useState("");
  const [reg, setReg] = useState("");

  // Facility fields
  const [facilityName, setFacilityName] = useState("");
  const [facilityType, setFacilityType] = useState("");
  const [address, setAddress] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [facilityPhone, setFacilityPhone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!tos) return toast.error("Please accept the Terms of Service");
    setSubmitting(true);
    try {
      const payload = { role, tos_accepted: true };
      const chosenCity = city || (supported_cities?.[0] || "Pune");
      if (!chosenCity) { toast.error("Pick a city"); setSubmitting(false); return; }
      if (role === "professional") {
        if (!specialty || !phone || !reg) {
          toast.error("Fill required fields");
          setSubmitting(false); return;
        }
        payload.council_registration_number = reg;
        payload.profile = {
          professional_type: profRole,
          specialty,
          degree,
          years_experience: Number(years) || 0,
          rate_inr_per_hour: Number(rate) || 0,
          phone: phone.startsWith("+91") ? phone : `+91${phone}`,
          city: chosenCity,
        };
      } else {
        if (!facilityName || !facilityType || !address || !facilityPhone) {
          toast.error("Fill required fields");
          setSubmitting(false); return;
        }
        payload.profile = {
          facility_name: facilityName,
          facility_type: facilityType,
          address,
          contact_person: contactPerson,
          phone: facilityPhone.startsWith("+91") ? facilityPhone : `+91${facilityPhone}`,
          city: chosenCity,
        };
      }
      await api.post("/onboarding", payload);
      await refresh();
      track("onboarding_completed", { role });
      toast.success("Profile saved");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <AppHeader subtitle="Get started" />
      <div className="max-w-3xl mx-auto px-5 py-10">
        {!role ? (
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Choose your role</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">How will you use LocumConnect?</h1>
            <p className="text-slate-600 mb-8">You can only pick one — this determines your workspace.</p>
            <div className="grid md:grid-cols-2 gap-5">
              <RoleCard
                testid="role-select-professional"
                icon={<Stethoscope className="w-5 h-5" />}
                title="Doctor or Nurse"
                desc="Apply to verified shifts around Pune. Get paid securely."
                onClick={() => setRole("professional")}
              />
              <RoleCard
                testid="role-select-facility"
                icon={<Building2 className="w-5 h-5" />}
                title="Hospital or Clinic"
                desc="Post shifts and hire pre-verified locum professionals."
                onClick={() => setRole("facility")}
              />
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                {role === "professional" ? "Professional profile" : "Facility profile"}
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">Tell us about yourself</h1>
              <button type="button" onClick={() => setRole(null)} className="text-sm text-emerald-800 underline mt-2" data-testid="onboarding-change-role">
                Change role
              </button>
            </div>

            <Card className="p-6 space-y-4 rounded-2xl border-slate-200">
              <Field label="City *">
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="onboard-city" className="h-11 rounded-xl"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>{supported_cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {role === "professional" ? (
                <>
                  <Field label="You are a">
                    <Select value={profRole} onValueChange={setProfRole}>
                      <SelectTrigger data-testid="onboard-prof-type" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="doctor">Doctor</SelectItem>
                        <SelectItem value="nurse">Nurse</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Specialty *">
                    <Select value={specialty} onValueChange={setSpecialty}>
                      <SelectTrigger data-testid="onboard-specialty" className="h-11 rounded-xl"><SelectValue placeholder="Select specialty" /></SelectTrigger>
                      <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Highest degree">
                    <Select value={degree} onValueChange={setDegree}>
                      <SelectTrigger data-testid="onboard-degree" className="h-11 rounded-xl"><SelectValue placeholder="Select your qualification" /></SelectTrigger>
                      <SelectContent>{DEGREES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Years of experience"><Input data-testid="onboard-years" type="number" value={years} onChange={e => setYears(e.target.value)} className="h-11 rounded-xl" /></Field>
                    <Field label="Rate (₹ / hour)"><Input data-testid="onboard-rate" type="number" value={rate} onChange={e => setRate(e.target.value)} className="h-11 rounded-xl" /></Field>
                  </div>
                  <Field label="Phone (+91) *"><Input data-testid="onboard-phone" placeholder="98XXXXXXXX" value={phone} onChange={e => setPhone(e.target.value)} className="h-11 rounded-xl" /></Field>
                  <Field label="Council registration number *"><Input data-testid="onboard-reg" placeholder="e.g. MMC-2019-12345" value={reg} onChange={e => setReg(e.target.value)} className="h-11 rounded-xl" /></Field>
                </>
              ) : (
                <>
                  <Field label="Facility name *"><Input data-testid="onboard-facility-name" value={facilityName} onChange={e => setFacilityName(e.target.value)} className="h-11 rounded-xl" /></Field>
                  <Field label="Facility type *">
                    <Select value={facilityType} onValueChange={setFacilityType}>
                      <SelectTrigger data-testid="onboard-facility-type" className="h-11 rounded-xl"><SelectValue placeholder="Choose type" /></SelectTrigger>
                      <SelectContent>{FACILITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Address *"><Input data-testid="onboard-address" value={address} onChange={e => setAddress(e.target.value)} className="h-11 rounded-xl" placeholder="Street, area" /></Field>
                  <Field label="Contact person"><Input data-testid="onboard-contact-person" value={contactPerson} onChange={e => setContactPerson(e.target.value)} className="h-11 rounded-xl" /></Field>
                  <Field label="Phone (+91) *"><Input data-testid="onboard-facility-phone" placeholder="98XXXXXXXX" value={facilityPhone} onChange={e => setFacilityPhone(e.target.value)} className="h-11 rounded-xl" /></Field>
                </>
              )}
            </Card>

            <Card className="p-5 rounded-2xl border-slate-200 bg-emerald-50/40 border-emerald-200">
              <div className="flex items-start gap-3">
                <Checkbox id="tos" data-testid="onboard-tos" checked={tos} onCheckedChange={setTos} className="mt-1" />
                <Label htmlFor="tos" className="text-sm leading-relaxed text-slate-700 font-normal">
                  I accept the Terms of Service. I understand LocumConnect is a matching platform;
                  the facility is responsible for clinical supervision and regulatory compliance for any locum engagement.
                </Label>
              </div>
            </Card>

            <Button data-testid="onboard-submit" type="submit" disabled={submitting} className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800">
              {submitting ? "Saving…" : "Save and continue"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function RoleCard({ icon, title, desc, onClick, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} className="text-left bg-white border border-slate-200 hover:border-emerald-500 hover:shadow-md transition-shadow rounded-2xl p-6">
      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-800 mb-4">{icon}</div>
      <div className="font-display text-xl font-bold tracking-tight">{title}</div>
      <p className="mt-2 text-sm text-slate-600">{desc}</p>
    </button>
  );
}
